import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';

const IskStep = ({ onNext, onBack }) => {
  const [iskDetails, setIskDetails] = useState({
    id: null,
    isk_number: "",
    isk_certificate: null,
  });
  const [isNextEnabled, setIsNextEnabled] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [existingCertificateUrl, setExistingCertificateUrl] = useState(null);
  const certificateInputRef = useRef();

  // Fetch ISK membership details on mount
  useEffect(() => {
    const fetchIskDetails = async () => {
      const apiUrl = "https://portal.lsb.go.ke/account/isk_membership/";
      const accessToken = localStorage.getItem("accessToken");

      try {
        const response = await fetch(apiUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || "Failed to fetch ISK details.");
        }

        const data = await response.json();
        if (data.length > 0) {
          const userData = data[0];
          setIskDetails((prev) => ({
            ...prev,
            id: userData.user_id,
            isk_number: userData.isk_number || "",
            isk_certificate: null,
          }));

          if (userData.isk_certificate && typeof userData.isk_certificate === "string") {
            setExistingCertificateUrl(userData.isk_certificate);
            setUploadedFileName(userData.isk_certificate.split("/").pop());
          }
          // Certificate is NOT required for Next (only isk_number)
          setIsNextEnabled(!!userData.isk_number);
        }
      } catch (error) {
        Swal.fire({
          title: "Error",
          text: error.message,
          icon: "error",
          confirmButtonText: "Retry",
        });
      }
    };

    fetchIskDetails();
    // eslint-disable-next-line
  }, []); // Run only once on mount

  // Validate inputs and update state
  const handleInputChange = (id, value) => {
    setIskDetails((prev) => ({
      ...prev,
      [id]: value,
    }));

    setTimeout(() => {
      // Certificate is not required; only check isk_number
      if (id === "isk_number") {
        setIsNextEnabled(!!value);
      } else {
        setIsNextEnabled(!!iskDetails.isk_number);
      }
    }, 0);
  };

  // Handle file input
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setUploadedFileName(file ? file.name : "");
    setExistingCertificateUrl(null);
    handleInputChange("isk_certificate", file || null);
  };

  const handleRemoveFile = () => {
    setIskDetails((prev) => ({
      ...prev,
      isk_certificate: null,
    }));
    setUploadedFileName("");
    certificateInputRef.current.value = "";
    // Don't affect isNextEnabled since certificate isn't required
  };

  const handleRemoveExistingCertificate = () => {
    setExistingCertificateUrl(null);
    setUploadedFileName("");
    // Don't affect isNextEnabled since certificate isn't required
  };

  const handleNext = async () => {
    const apiUrl = "https://portal.lsb.go.ke/account/isk_membership/";
    const personalApiUrl = "https://portal.lsb.go.ke/account/personal_data/";
    const accessToken = localStorage.getItem("accessToken");

    const formData = new FormData();
    formData.append("id", iskDetails.id);
    formData.append("isk_number", iskDetails.isk_number);
    // Certificate optional
    if (iskDetails.isk_certificate) {
      formData.append("isk_certificate", iskDetails.isk_certificate);
    }
    // --- ADD THESE FIELDS AS REQUIRED ---
    formData.append("isk_data_completed", true);
    formData.append("step_4", true);

    try {
      const response = await fetch(apiUrl, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to upload ISK details.");
      }

      const user_id = localStorage.getItem("user_id") || iskDetails.id;
      if (user_id) {
        const formStep4 = new FormData();
        formStep4.append("id", user_id);
        formStep4.append("step_4", true);
        formStep4.append("isk_membership_completed", true);

        const stepResponse = await fetch(personalApiUrl, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formStep4,
        });

        if (!stepResponse.ok) {
          throw new Error("Failed to mark step 4 as completed.");
        }
      }

      Swal.fire({
        title: "Success",
        text: "ISK details updated successfully!",
        icon: "success",
        confirmButtonText: "OK",
      });

      onNext();
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error.message,
        icon: "error",
        confirmButtonText: "Retry",
      });
    }
  };

  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    Swal.fire({
      title: 'Success',
      text: 'You have been logged out.',
      icon: 'success',
      width: 370,
    });
    navigate('/portal');
  };

  return (
    <div className="px-4 lg:px-8 py-4 container mx-auto">
      <div className="flex justify-between items-center pb-6">
        <h3 className="font-bold text-[#3d0707] tracking-wide underline underline-offset-4 decoration-[#ebcab3]">
          ISK Membership Details
        </h3>

        <button
          onClick={handleLogout}
          className="py-2 px-3 bg-red-600 hover:font-bold uppercase text-white flex gap-2 items-center rounded-md text-sm transition-transform duration-200 ease-in-out hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-red-400 shadow-sm hover:shadow-lg"
        >
          <span className="transition duration-200 ease-in-out group-hover:pr-1">Logout</span>
          <LogOut className="w-5 h-5 transition-transform duration-200 group-hover:rotate-[-15deg]" />
        </button>
      </div>

      <form
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        {/* ISK Membership Number */}
        <div>
          <label className="block text-gray-600 font-medium mb-1" htmlFor="isk_number">
            ISK Membership Number <span className="text-red-600 text-xl">*</span>
          </label>
          <input
            id="isk_number"
            value={iskDetails.isk_number}
            placeholder="Enter your membership number"
            className="mt-1 w-full rounded-md border border-gray-300 bg-white text-base text-gray-800 shadow-sm font-semibold focus:ring-2 focus:ring-sky-200 focus:border-sky-400 transition duration-100"
            onChange={(e) => handleInputChange("isk_number", e.target.value)}
            autoComplete="off"
          />
        </div>

        {/* ISK Membership Certificate */}
        <div>
          <label className="block text-gray-600 font-medium mb-1" htmlFor="isk_certificate">
            ISK Full Membership Certificate
            <span className="block text-xs text-gray-400 font-normal">
              Upload PDF only. Max size: 10MB. (Optional)
            </span>
          </label>
          <div className="flex items-center gap-4">
            <input
              id="isk_certificate"
              ref={certificateInputRef}
              type="file"
              accept=".pdf"
              className="block w-full text-sm bg-white file:bg-sky-50 file:rounded file:border-none file:py-2 file:px-4 border border-gray-200 text-gray-800 file:font-semibold focus:outline-sky-400 transition"
              onChange={handleFileChange}
              key={existingCertificateUrl ? "withurl" : "noupload"}
            />
            {(!!uploadedFileName || !!existingCertificateUrl) && (
              <button
                type="button"
                className="px-2 py-2 bg-red-800 text-white text-sm font-semibold border border-red-200 hover:bg-red-200 transition"
                aria-label="Remove selected file"
                onClick={
                  existingCertificateUrl ? handleRemoveExistingCertificate : handleRemoveFile
                }
              >
                Remove
              </button>
            )}
          </div>
          {/* File name and link if present */}
          <div className="mt-2 flex items-center gap-2">
            {uploadedFileName && (
              <span className="inline-block bg-[#ebcab3] text-[#3d0707] text-xs p-2 font-bold">
                {uploadedFileName}
              </span>
            )}
            {!uploadedFileName && existingCertificateUrl && (
              <a
                href={existingCertificateUrl}
                className="text-xs text-sky-700 underline font-semibold"
                target="_blank"
                rel="noopener noreferrer"
                title="View current certificate"
              >
                Existing certificate
              </a>
            )}
          </div>
        </div>
      </form>

      {/* Navigation Buttons */}
      <div className="pt-8 flex gap-3">
        <button type="button" onClick={onBack} className="button">
          <span className="button_lg">
            <span className="button_sl"></span>
            Back
          </span>
        </button>

        <button
          type="button"
          onClick={handleNext}
          className={`button ${isNextEnabled ? "" : "opacity-50 cursor-not-allowed"}`}
          disabled={!isNextEnabled}
        >
          <span className="button_lg">
            <span className="button_sl"></span>
            Next
          </span>
        </button>
      </div>
    </div>
  );
};

export default IskStep;